/**
 * Overview tab — the live security triage cockpit landing screen.
 *
 * Layout:
 *   1. Pulse strip — 13 KPI tiles with sparklines and Δ vs prev window.
 *   2. Top high-risk events — last-24h high-band requests by default,
 *      live filters when any are active.
 *   3. Risk timeseries — stacked area for total / suspicious / high.
 *   4. Global Activity SECTION (not a tab) — world map + country table
 *      + metric toggle.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useUrlState } from "@/hooks/useUrlState";
import { pivotTo } from "@/lib/pivot";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton, Badge } from "@/components/primitives";
import { fmtNumber, fmtPct, fmtSignedPct, fmtLatency } from "@/lib/format";
import { HIGH_RISK_THRESHOLD, SUSPICIOUS_THRESHOLD } from "@/config/riskBands";
import { endpointAreaGroups } from "@/config/filters";
import { GlobalActivitySection } from "@/components/overview/GlobalActivitySection";
import { RiskTimeseriesChart } from "@/components/overview/RiskTimeseriesChart";
import { PriorityBrief } from "@/components/overview/PriorityBrief";
import { TopHighRiskEventsCard } from "@/components/overview/TopHighRiskEventsCard";
import { TargetedAccountsCard } from "@/components/overview/TargetedAccountsCard";
import { DistributedToolingCard } from "@/components/overview/DistributedToolingCard";
import { RepeatOffendersCard } from "@/components/overview/RepeatOffendersCard";
import type { EntityTypeV1 } from "@/types/v1";

/**
 * Map a clicked entity type to the URL filter key Investigations reads.
 * Mirrors the ENTITY_TO_FILTER table in routes/Investigations.tsx.
 */
const ENTITY_TO_URL_KEY: Partial<Record<EntityTypeV1, string>> = {
  ip: "ip",
  asn: "asn",
  ja4: "ja4",
  ja3: "ja3",
  email: "email",
  business: "businessId",
  session: "sessionId",
  country: "country",
  managedList: "managedList",
  detectionId: "detectionId",
  endpointGroup: "endpointGroups",
};

export function Overview() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const [, patchUrl] = useUrlState();

  // Cross-tab pivot: clicking an entity on any Overview card narrows
  // the Investigations table to that entity and switches tab. Drawer
  // is NOT opened automatically — user opens it from the events row
  // if they want the deep dive (matches the Trends/Campaigns pattern).
  const onEntity = useCallback((type: EntityTypeV1, value: string) => {
    const key = ENTITY_TO_URL_KEY[type];
    if (!key) return;
    const patch = key === "endpointGroups"
      ? { endpointGroups: endpointAreaGroups(value).join(",") }
      : { [key]: value };
    pivotTo(patchUrl, patch, "investigations");
  }, [patchUrl]);

  const summary = useQuery({
    queryKey: ["summary", filters, tick],
    queryFn: ({ signal }) => api.summary(filters, signal),
  });

  const ts = useQuery({
    queryKey: ["timeseries", filters, tick],
    queryFn: ({ signal }) => api.timeseries(filters, signal),
  });

  return (
    <div className="flex flex-col gap-5">
      <PulseStrip
        loading={summary.isLoading}
        error={summary.error}
        data={summary.data?.data}
        onRetry={() => summary.refetch()}
      />

      <PriorityBrief />

      <TopHighRiskEventsCard onEntity={onEntity} />

      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-sm font-semibold text-fg">Where to investigate next</h2>
          <p className="mt-0.5 text-xs text-fg-muted">Smaller focused tables for accounts, shared tooling, and recurring entities.</p>
        </div>
        <div className="grid gap-5 xl:grid-cols-2 2xl:grid-cols-3">
          <TargetedAccountsCard onEntity={onEntity} />
          <DistributedToolingCard onEntity={onEntity} />
          <RepeatOffendersCard onEntity={onEntity} />
        </div>
      </section>

      <Card>
        <CardHeader
          title="Risk over time"
          subtitle="Stacked by risk band — sampled counts weighted by _sample_interval"
        />
        {ts.isLoading ? (
          <Skeleton className="h-72" />
        ) : ts.error ? (
          <ErrorState error={ts.error} onRetry={() => ts.refetch()} />
        ) : !ts.data?.data?.length ? (
          <EmptyState title="No events in this window." />
        ) : (
          <RiskTimeseriesChart points={ts.data.data} />
        )}
      </Card>

      <GlobalActivitySection />
    </div>
  );
}

/* ----------------------------------------------------------------------- */

function PulseStrip({ loading, error, data, onRetry }: {
  loading: boolean;
  error: unknown;
  data?: import("@/types/v1").SummaryV1;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }
  if (error) {
    return (
      <Card><ErrorState error={error} onRetry={onRetry} /></Card>
    );
  }
  if (!data) {
    return <Card><EmptyState title="Summary unavailable." /></Card>;
  }

  const tiles = useMemo(() => buildTiles(data), [data]);

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      {tiles.map((t) => (
        <Card key={t.label} className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs font-medium text-fg-subtle">{t.label}</div>
            {t.tone && <Badge tone={t.tone}>{t.toneLabel}</Badge>}
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-mono text-2xl font-semibold text-fg">{t.value}</div>
            {t.delta != null && (
              <span className={
                t.delta === 0 ? "text-xs text-fg-subtle" :
                t.delta > 0 ? "text-xs text-sev-watch" :
                "text-xs text-sev-good"
              }>{fmtSignedPct(t.delta)}</span>
            )}
          </div>
          {t.hint && <div className="mt-1 text-[11px] text-fg-subtle">{t.hint}</div>}
        </Card>
      ))}
    </div>
  );
}

function buildTiles(s: import("@/types/v1").SummaryV1) {
  const highPct = s.totalEvents ? s.highRisk / s.totalEvents : 0;
  const suspPct = s.totalEvents ? s.suspicious / s.totalEvents : 0;
  return [
    { label: "Total events",       value: fmtNumber(s.totalEvents),  delta: s.delta?.totalEvents ?? null, hint: "All scored requests in window" },
    { label: "High risk",          value: fmtNumber(s.highRisk),     delta: s.delta?.highRisk ?? null, hint: `score ≥ ${HIGH_RISK_THRESHOLD}`, tone: "high" as const, toneLabel: fmtPct(highPct, 1) },
    { label: "Suspicious",         value: fmtNumber(s.suspicious),   hint: `score ${SUSPICIOUS_THRESHOLD}–${HIGH_RISK_THRESHOLD - 1}`, tone: "suspicious" as const, toneLabel: fmtPct(suspPct, 1) },
    { label: "Challenge candidates", value: fmtNumber(s.challengeCandidates), hint: "action = challenge_candidate" },
    { label: "Block candidates",   value: fmtNumber(s.blockCandidates),       hint: "action = block_candidate" },
    { label: "Distinct IPs",       value: fmtNumber(s.distinctIps),  hint: "approx — sampled" },
    { label: "Distinct ASNs",      value: fmtNumber(s.distinctAsns), hint: "approx — sampled" },
    { label: "Distinct JA4",       value: fmtNumber(s.distinctJa4),  hint: "TLS fingerprints" },
    { label: "Distinct emails",    value: fmtNumber(s.distinctEmails) },
    { label: "Managed-list hits",  value: fmtNumber(s.managedListHits), hint: "Cloudflare threat lists" },
    { label: "Verified bot share", value: fmtPct(s.verifiedBotShare, 2), hint: "of total events" },
    { label: "p95 risk score",     value: String(Math.round(s.p95RiskScore)), hint: "weighted percentile" },
    { label: "p95 scoring latency",value: fmtLatency(s.p95ScoringLatencyMs), hint: "scorer end-to-end" },
  ];
}
